import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import Countdown from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Play, CalendarDays } from "lucide-react";
import { countryFlagUrl } from "@/lib/countries";

// Curated public-domain fallbacks per info-page slug.
// Admins can override any of these by uploading a header image on the corresponding info page.
const FALLBACK = {
  venue: "https://images.unsplash.com/photo-1559692048-79a3f837883d?auto=format&fit=crop&w=1200&q=70", // Helsinki Cathedral
  about: "https://images.unsplash.com/photo-1601106605555-9a09a2dbd1d4?auto=format&fit=crop&w=1200&q=70", // Helsinki harbour
  youth: "https://images.unsplash.com/photo-1611996575749-79a3a250f948?auto=format&fit=crop&w=1200&q=70", // young player concept
  prizes: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?auto=format&fit=crop&w=1200&q=70", // trophy
};

function stripHtml(html = "") {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}
function pageSnippet(page, max = 110) {
  if (!page) return "";
  const text = stripHtml(page.content_html || "");
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}
function pageImage(page, fallback) {
  if (page?.image_path) return imageUrl(page.image_path);
  return fallback || null;
}

export default function HomePage() {
  const { current } = useChampionship() || {};
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [teams, setTeams] = useState([]);
  const [infoPages, setInfoPages] = useState([]);

  useEffect(() => {
    if (!current?.id) return;
    api.get(`/championships/${current.id}/news`).then((r) => setNews(r.data || [])).catch(() => {});
    api.get(`/championships/${current.id}/sponsors`).then((r) => setSponsors(r.data || [])).catch(() => {});
    api.get(`/championships/${current.id}/teams`).then((r) => setTeams(r.data || [])).catch(() => {});
    api.get(`/championships/${current.id}/info-pages`).then((r) => setInfoPages(r.data || [])).catch(() => {});
  }, [current?.id]);

  const teamsCount = teams.length;
  const playersCount = teams.reduce((a, t) => a + t.players.length, 0);
  const featured = news.filter((n) => n.is_featured).slice(0, 1)[0];
  const recent = news.filter((n) => n !== featured).slice(0, 3);
  const latestNews = news[0];
  const infoBy = Object.fromEntries(infoPages.map((p) => [p.slug, p]));
  const heroUrl = current?.hero_image_path ? imageUrl(current.hero_image_path) : null;
  const targetDate = current?.start_date ? `${current.start_date}T09:00:00` : "2026-11-04T09:00:00";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex flex-col text-white overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0">
          {/* Local CSS placeholder — shown immediately so no remote round-trip flash before data loads */}
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-br from-[#0c1a14] via-[#0a0a0a] to-[#000]"
          />
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 25% 30%, rgba(0,138,69,0.25), transparent 35%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.05), transparent 30%)",
            }}
          />
          {heroUrl && (
            <img
              key={heroUrl}
              src={heroUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700"
              onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
            />
          )}
          {/* Stronger gradient + subtle vignette so top/bottom text stays legible on any hero image */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/85" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent" />
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-between max-w-7xl mx-auto w-full px-6 lg:px-10 py-12">
          <div className="flex items-center justify-between text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
            <span>World Othello Championship</span>
            <span>{current?.start_date && new Date(current.start_date).toLocaleDateString(undefined, { day: "numeric", month: "long" })} — {current?.end_date && new Date(current.end_date).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
          <div className="grid md:grid-cols-12 gap-10 items-end mt-auto">
            <div className="md:col-span-8">
              <h1 className="font-display font-extrabold tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.9] drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                {current?.location?.toUpperCase() || "HELSINKI"}
              </h1>
              <p className="mt-8 font-display font-bold tracking-tight text-2xl md:text-3xl lg:text-4xl text-white max-w-2xl leading-snug drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                {current?.tagline || "The 48th World Othello Championship."}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                {current?.live_standings_url && (
                  <a href={current.live_standings_url} target="_blank" rel="noreferrer">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6" data-testid="hero-live-standings">
                      LIVE Standings <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                )}
                <Link to="/teams">
                  <Button variant="outline" className="bg-white/0 border-white/40 text-white hover:bg-white hover:text-foreground rounded-md px-6" data-testid="hero-view-teams">
                    View Teams
                  </Button>
                </Link>
                {current?.live_stream_url && (
                  <a href={current.live_stream_url} target="_blank" rel="noreferrer">
                    <Button variant="ghost" className="text-white hover:bg-white/10 rounded-md" data-testid="hero-watch-live">
                      <Play className="mr-2 h-4 w-4 fill-white" /> Watch Live
                    </Button>
                  </a>
                )}
              </div>
            </div>
            <div className="md:col-span-4 md:border-l md:border-white/15 md:pl-10">
              <Countdown target={targetDate} label="Tournament begins in" />
            </div>
          </div>
        </div>
      </section>

      {/* QUICK STATS */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Edition", value: `${current?.year || "—"}` },
            { label: "Host city", value: current?.location || "—" },
            { label: "Countries", value: teamsCount || "—" },
            { label: "Players", value: playersCount || "—" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col" data-testid={`stat-${s.label}`}>
              <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{s.label}</span>
              <span className="font-display text-3xl md:text-4xl font-bold mt-1 tracking-tight">{s.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* INFO BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Information</h2>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground hidden md:block">
            Everything you need to know
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[200px]">
          {/* TEAMS — tall, dark, with country flag previews */}
          <TeamsBentoCard
            teams={teams}
            teamsCount={teamsCount}
            playersCount={playersCount}
            className="md:col-span-6 md:row-span-2"
          />

          {/* NEWS — surface latest headline */}
          <BentoCard
            to="/news"
            eyebrow="News"
            title={latestNews ? latestNews.title : "Latest from the championship"}
            snippet={latestNews?.excerpt || "Read the most recent updates from the organising committee."}
            image={latestNews?.image_path ? imageUrl(latestNews.image_path) : null}
            theme="primary"
            className="md:col-span-6 md:row-span-1"
            testid="bento-news"
          />

          {/* VENUE — image from info page or fallback */}
          <BentoCard
            to="/info/venue"
            eyebrow="Venue"
            title={current?.location || "Helsinki"}
            snippet={pageSnippet(infoBy.venue) || `Hosting venue in ${current?.country || "Finland"}.`}
            image={pageImage(infoBy.venue, FALLBACK.venue)}
            theme="image"
            className="md:col-span-3 md:row-span-1"
            testid="bento-venue"
          />

          {/* SCHEDULE — date layout */}
          <ScheduleBentoCard
            startDate={current?.start_date}
            endDate={current?.end_date}
            className="md:col-span-3 md:row-span-1"
          />

          {/* ABOUT HOST CITY */}
          <BentoCard
            to="/info/about-helsinki"
            eyebrow={`About ${current?.location || "Helsinki"}`}
            title="Discover the host city"
            snippet={pageSnippet(infoBy["about-helsinki"]) || "Discover the host city, its people, design and Othello community."}
            image={pageImage(infoBy["about-helsinki"], FALLBACK.about)}
            theme="image"
            className="md:col-span-6 md:row-span-1"
            testid="bento-about-helsinki"
          />

          {/* YOUTH WOC */}
          <BentoCard
            to="/info/youth-woc"
            eyebrow="Youth WOC"
            title="Future champions"
            snippet={pageSnippet(infoBy["youth-woc"]) || "Players under 18 compete in their own division alongside the main event."}
            image={pageImage(infoBy["youth-woc"], FALLBACK.youth)}
            theme="dark-image"
            className="md:col-span-3 md:row-span-1"
            testid="bento-youth"
          />

          {/* PRIZES */}
          <BentoCard
            to="/info/prizes"
            eyebrow="Prizes"
            title="Trophy & podium"
            snippet={pageSnippet(infoBy.prizes) || "Titles, trophies and medals across the Open, Women's and Youth championships."}
            image={pageImage(infoBy.prizes, FALLBACK.prizes)}
            theme="dark-image"
            className="md:col-span-3 md:row-span-1"
            testid="bento-prizes"
          />
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section className="bg-secondary">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
            <div className="flex items-end justify-between mb-12">
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Latest News</h2>
              <Link to="/news" className="text-sm font-medium hover:text-primary inline-flex items-center gap-1" data-testid="home-news-all">
                All news <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {featured && (
                <Link to={`/news/${featured.slug}`} className="lg:col-span-7 group" data-testid={`news-featured-${featured.slug}`}>
                  <div className="aspect-[16/10] bg-neutral-200 overflow-hidden border border-border">
                    {featured.image_path ? (
                      <img src={imageUrl(featured.image_path)} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="h-full w-full bg-foreground" />
                    )}
                  </div>
                  <div className="mt-6">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-3">Featured</div>
                    <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight group-hover:text-primary transition-colors">{featured.title}</h3>
                    <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>
                  </div>
                </Link>
              )}
              <div className="lg:col-span-5 space-y-8">
                {recent.map((n) => (
                  <Link key={n.id} to={`/news/${n.slug}`} className="group block" data-testid={`news-card-${n.slug}`}>
                    <div className="border-t border-border pt-6">
                      <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">
                        {new Date(n.published_at).toLocaleDateString()}
                      </div>
                      <h3 className="font-display text-xl font-bold group-hover:text-primary transition-colors">{n.title}</h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{n.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS */}
      {sponsors.length > 0 && (
        <section className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground text-center mb-12">
              Official partners
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-10">
              {sponsors.map((s) => (
                <a
                  key={s.id}
                  href={s.url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="opacity-70 hover:opacity-100 transition-opacity"
                  data-testid={`sponsor-${s.id}`}
                >
                  {s.logo_path ? (
                    <img src={imageUrl(s.logo_path)} alt={s.name} className="h-10 md:h-12 object-contain transition" />
                  ) : (
                    <span className="font-display text-lg font-semibold tracking-tight">{s.name}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function BentoCard({ to, eyebrow, title, snippet, image, theme = "secondary", className = "", testid }) {
  // theme: "secondary" plain | "primary" green flat | "image" image + light gradient | "dark-image" image + dark gradient
  const hasImage = !!image && (theme === "image" || theme === "dark-image");
  const base = "group relative overflow-hidden p-7 md:p-8 flex flex-col justify-between transition-all hover:-translate-y-1";
  const themeCls = {
    secondary: "bg-secondary text-foreground",
    primary: "bg-primary text-primary-foreground",
    image: "text-white",
    "dark-image": "text-white",
  }[theme];

  return (
    <Link to={to} className={`${base} ${themeCls} ${className}`} data-testid={testid}>
      {hasImage && (
        <>
          <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" />
          <div className={`absolute inset-0 ${theme === "dark-image" ? "bg-gradient-to-t from-black/90 via-black/50 to-black/30" : "bg-gradient-to-t from-black/85 via-black/40 to-black/10"}`} />
        </>
      )}
      <div className={`relative ${hasImage ? "drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" : ""}`}>
        <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">{eyebrow}</div>
      </div>
      <div className={`relative ${hasImage ? "drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" : ""}`}>
        <div className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter leading-[1.05]">
          {title}
        </div>
        {snippet && (
          <p className="mt-2 text-xs md:text-sm opacity-90 line-clamp-2 leading-snug">
            {snippet}
          </p>
        )}
        <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium opacity-90 group-hover:opacity-100">
          Explore <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function TeamsBentoCard({ teams, teamsCount, playersCount, className = "" }) {
  const previewFlags = teams.slice(0, 12);
  return (
    <Link
      to="/teams"
      className={`group relative overflow-hidden p-7 md:p-9 flex flex-col justify-between bg-[#0A0A0A] text-white transition-all hover:-translate-y-1 ${className}`}
      data-testid="bento-teams"
    >
      <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-primary/20 blur-3xl opacity-60" />
      <div className="absolute -left-16 bottom-0 w-72 h-72 rounded-full bg-white/5 blur-3xl" />

      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Teams</div>
        <div className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter mt-3 leading-none">
          {teamsCount} <span className="text-white/40 font-medium text-2xl md:text-3xl align-middle">countries</span>
        </div>
        <div className="mt-2 text-sm text-white/70">
          {playersCount} player{playersCount !== 1 ? "s" : ""} confirmed
        </div>
      </div>

      <div className="relative">
        {previewFlags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mb-6 max-w-md">
            {previewFlags.map((t) => (
              <div key={t.country_code} className="h-7 w-10 overflow-hidden border border-white/20" title={t.country_name}>
                <img src={countryFlagUrl(t.country_code)} alt={t.country_name} className="h-full w-full object-cover" />
              </div>
            ))}
            {teams.length > previewFlags.length && (
              <div className="h-7 px-2 inline-flex items-center text-xs text-white/70 border border-white/20">
                +{teams.length - previewFlags.length}
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-white/50 mb-6">Federation rosters will appear here as captains submit them.</div>
        )}
        <div className="inline-flex items-center gap-2 text-sm font-medium text-white group-hover:text-primary transition-colors">
          View all teams <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function ScheduleBentoCard({ startDate, endDate, className = "" }) {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const fmtDay = (d) => (d ? d.toLocaleDateString(undefined, { day: "numeric" }) : "");
  const fmtMonth = (d) => (d ? d.toLocaleDateString(undefined, { month: "short" }).toUpperCase() : "");
  const fmtYear = (d) => (d ? d.toLocaleDateString(undefined, { year: "numeric" }) : "");

  return (
    <Link
      to="/info/schedule"
      className={`group relative overflow-hidden p-7 md:p-8 flex flex-col justify-between bg-foreground text-background transition-all hover:-translate-y-1 ${className}`}
      data-testid="bento-schedule"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Schedule</div>

      {start && end ? (
        <div className="my-3">
          <div className="flex items-baseline gap-3">
            <div className="font-display text-5xl md:text-6xl font-extrabold tracking-tighter font-mono-tab leading-none">{fmtDay(start)}</div>
            <div className="text-2xl opacity-40">→</div>
            <div className="font-display text-5xl md:text-6xl font-extrabold tracking-tighter font-mono-tab leading-none">{fmtDay(end)}</div>
          </div>
          <div className="mt-2 text-xs uppercase tracking-[0.2em] opacity-80">
            {fmtMonth(start)} {fmtYear(start)}
          </div>
        </div>
      ) : (
        <div className="my-3 flex items-center gap-2 opacity-60">
          <CalendarDays className="h-5 w-5" /> Dates to be confirmed
        </div>
      )}

      <div className="inline-flex items-center gap-2 text-xs font-medium opacity-90 group-hover:opacity-100">
        Full schedule <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </div>
    </Link>
  );
}
